/**
 * Settings page editors of the batch tool defaults (W2-11 batch-tools): `ext.batch.bakeDensity` and
 * `ext.batch.pathPerBeat`.
 *
 * The page's default number editor shows 0 for a value that was never stored, while the tools use their default
 * (8). These editors read through `extNumber` instead, so the field always shows the value the tools use; entering
 * the default clears the stored value, so Reset and the "changed" mark stay right.
 */

import { useCallback, useSyncExternalStore, type ComponentType } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { SettingPath } from '../../app/settings/types';
import { Expr } from '../../core/expr/index';
import type { MessageKey } from '../../i18n/types';
import { useT } from '../../i18n/useT';
import { NumberField } from '../../ui/NumberField';
import { extNumber } from './batchContext';

export interface ExtNumberSpec {
  /** `settings.ext` key ('batch.<name>'). */
  readonly key: string;
  readonly def: number;
  readonly min: number;
  readonly max: number;
  /** Accessible name of the field (the row title). */
  readonly title: MessageKey;
}

/** Writes a value: the default clears the stored value, anything else is stored (integers within the range). */
export function writeExtNumber(
  s: Pick<AppServices['settings'], 'getExt' | 'setExt' | 'reset'>,
  spec: ExtNumberSpec,
  v: number,
): void {
  if (!Number.isFinite(v)) return;
  const next = Math.min(spec.max, Math.max(spec.min, Math.round(v)));
  if (next === spec.def) {
    if (s.getExt(spec.key) !== undefined) s.reset(`ext.${spec.key}` as SettingPath);
    return;
  }
  s.setExt(spec.key, next);
}

/** The settings page editor of one remembered integer. */
export function extNumberEditor(spec: ExtNumberSpec): ComponentType<{ readonly services: AppServices }> {
  function ExtNumberEditor(props: { readonly services: AppServices }) {
    const { services } = props;
    const t = useT();
    const subscribe = useCallback((cb: () => void) => services.settings.subscribe(cb), [services]);
    const read = useCallback(() => extNumber(services, spec.key, spec.def, spec.min, spec.max), [services]);
    const value = useSyncExternalStore(subscribe, read, read);
    return (
      <NumberField
        stacked
        label={<span className="visually-hidden">{t(spec.title)}</span>}
        value={{ kind: 'value', value }}
        vars="field"
        min={spec.min}
        max={spec.max}
        step={1}
        precision={0}
        onCommit={(c) => {
          if (c.kind === 'number') {
            writeExtNumber(services.settings, spec, c.value);
            return;
          }
          const out = Expr.apply(c.program, { v: value, i: 0, n: 1, u: 0 }, Expr.rng(1));
          if (out !== null) writeExtNumber(services.settings, spec, out);
        }}
      />
    );
  }
  ExtNumberEditor.displayName = `ExtNumberEditor(${spec.key})`;
  return ExtNumberEditor;
}
